import * as Sentry from '@sentry/react';
import { useSuspenseQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import * as React from 'react';
import { useRecoilValue } from 'recoil';
import rison from 'rison';

import type * as api from '@gitbook/api-client';
import { Icon } from '@gitbook/spine-icons';
import { Description, EmptyState, EmptyStateIcon, Heading } from '@gitbook/spine-react';

import { apiQuery } from '../../state';
import { stripObjectFromUndefined } from '../../utils';
import { siteVisitorSegmentSelector } from '../AdaptiveContent';
import { APIErrorBoundary } from '../APIErrorBoundary';
import type { DeviceResolution } from './devices';
import { getSiteURLForContent, type SitePreviewContent } from './getSiteURLForContent';
import { Iframe } from './Iframe';
import { sitePreviewAtom } from './state';

export interface SitetPreviewIframeProps {
    site: api.Site;
    organization: api.Organization;

    /**
     * Content to display in the iframe.
     */
    content?: SitePreviewContent;

    /**
     * Customization settings to apply to the preview.
     */
    customization?: Partial<api.SiteCustomizationSettings>;

    /**
     * Visitor claims to apply to the preview.
     */
    claims?: api.PlainObject;

    /**
     * Class name to apply to the iframe.
     */
    className?: string;

    /**
     * If true, the user can interact with the iframe.
     */
    interactive?: boolean;

    /**
     * Screen resolution to use within the iframe.
     */
    scaleResolution?: DeviceResolution;
}

/**
 * Iframe that displays the site preview.
 */
export function SitePreviewIframe(props: SitetPreviewIframeProps) {
    const { className, scaleResolution } = props;

    const { theme } = useRecoilValue(sitePreviewAtom);

    if (process.env.GITBOOK_PREDICTABLE_UI) {
        return <div className={clsx('bg-muted', className)} />;
    }

    return (
        <Sentry.ErrorBoundary
            fallback={
                <SitePreviewIframeFallback className={className}>
                    <UnknownErrorEmptyState />
                </SitePreviewIframeFallback>
            }
        >
            <APIErrorBoundary
                resetKeys={[props.site.id, props.content, props.claims, props.customization, theme]}
                fallback={{
                    404: (
                        <SitePreviewIframeFallback className={className}>
                            <NoContentEmptyState />
                        </SitePreviewIframeFallback>
                    ),
                    401: (
                        <SitePreviewIframeFallback className={className}>
                            <RestrictedAccessEmptyState site={props.site} />
                        </SitePreviewIframeFallback>
                    ),
                    412: (
                        <Iframe
                            src={null}
                            interactive={false}
                            scaleResolution={scaleResolution}
                            className={className}
                        />
                    ),
                }}
            >
                <React.Suspense fallback={<div className={clsx('shimmer', className)} />}>
                    <SitePreviewIframeWithData {...props} theme={theme} />
                </React.Suspense>
            </APIErrorBoundary>
        </Sentry.ErrorBoundary>
    );
}

function SitePreviewIframeWithData(
    props: SitetPreviewIframeProps & { theme: api.CustomizationThemeMode }
) {
    const {
        site,
        organization,
        theme,
        claims,
        content = {},
        customization,
        className,
        interactive = false,
        scaleResolution,
    } = props;

    const { data } = useSuspenseQuery(
        apiQuery('getSitePublishingPreviewById', {
            organizationId: organization.id,
            siteId: site.id,
            siteSpace: content.siteSpaceId,
            ...(typeof claims !== 'undefined' ? { claims: rison.encode_object(claims) } : {}),
        })
    );

    const previewURL = React.useMemo(() => {
        if (!data?.url) {
            return null;
        }

        const url = getSiteURLForContent(data.url, {
            siteSpaceId: content.siteSpaceId,
            revisionId: content.revisionId,
            pagePath: content.pagePath,
        });

        // rison does not like undefined values, but our customization logic can produce them. Strip them out before encoding.
        if (customization) {
            url.searchParams.append(
                'customization',
                rison.encode_object(stripObjectFromUndefined(customization))
            );
        }

        if (theme) {
            url.searchParams.append('theme', theme);
        }
        return url.toString();
    }, [
        data?.url,
        customization,
        theme,
        content.siteSpaceId,
        content.revisionId,
        content.pagePath,
    ]);

    if (!previewURL) {
        <SitePreviewIframeFallback className={className}>
            <UnknownErrorEmptyState />
        </SitePreviewIframeFallback>;
    }

    return (
        <Iframe
            src={previewURL}
            interactive={interactive}
            scaleResolution={scaleResolution}
            className={className}
        />
    );
}

function SitePreviewIframeFallback(
    props: React.PropsWithChildren<{ className: SitetPreviewIframeProps['className'] }>
) {
    return (
        <div className={clsx('shimmer flex items-center justify-center', props.className)}>
            {props.children}
        </div>
    );
}

function UnknownErrorEmptyState() {
    return (
        <EmptyState>
            <EmptyStateIcon>
                <Icon.Warning />
            </EmptyStateIcon>
            <Heading>Error when previewing site</Heading>
            <Description>
                Something went wrong while loading the preview of the site. Please try reloading the
                app.
            </Description>
        </EmptyState>
    );
}

function NoContentEmptyState() {
    return (
        <EmptyState>
            <EmptyStateIcon>
                <Icon.EyeOff />
            </EmptyStateIcon>
            <Heading>No content in your site yet</Heading>
            <Description>Add a space to your site to be able to preview it.</Description>
        </EmptyState>
    );
}

function RestrictedAccessEmptyState(props: { site: api.Site }) {
    const { site } = props;
    const visitorSegment = useRecoilValue(siteVisitorSegmentSelector(site.id));

    return (
        <EmptyState>
            <EmptyStateIcon>
                <Icon.Condition />
            </EmptyStateIcon>
            <Heading>Segment can’t access this content</Heading>
            <Description>
                This site isn’t available when previewing as “
                {visitorSegment?.title ?? 'Default experience'}”.
                <br />
                Select another test visitor segment to be able to preview it.
            </Description>
        </EmptyState>
    );
}
